import { useCallback, useEffect, useState } from "react";
import { BookOpen, PenLine, Hash, Library, Loader2, ChevronRight, ClipboardCheck } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { getFLNLearnerState, updateFLNCursor } from "@/lib/api";
import FLNAssessmentModal from "@/components/FLNAssessmentModal";

// ─── Level sequences (hardcoded, linear) ─────────────────────────────────────
const FLN_LEVELS = {
  reading:  ["R1","R2","R3","R4","R5","R6","R7","R8","R9","R10"],
  writing:  ["W1","W2","W3","W4","W5","W6","W7","W8","W9","W10","W11","W12","W13"],
  counting: ["C1","C2","C3","C4","C5","C6","C7","C8","C9","C10","C11","C12","C13","C14","C15","C16","C17","C18"],
};

const FLN_COMPETENCIES = {
  reading: {
    R1: "Read alphabets",
    R2: "Read 3-letter words",
    R3: "Read 4-letter words",
    R4: "Read complex words",
    R5: "Read simple sentences (3-letter words)",
    R6: "Read simple sentences (4-letter words)",
    R7: "Read small paragraph",
    R8: "Read paragraph with tone and intonation",
    R9: "Read paragraph with punctuation awareness",
    R10: "Comprehension reading",
  },
  writing: {
    W1: "Write alphabets",
    W2: "Write 3-letter words",
    W3: "Write 4-letter words",
    W4: "Write complex words",
    W5: "Recognise and write digraphs",
    W6: "Write words with digraphs",
    W7: "Write simple sentences",
    W8: "Write sentences with blends",
    W9: "Write sentences with digraphs",
    W10: "Write long and complex sentences",
    W11: "Paragraph writing",
    W12: "Punctuation in writing",
    W13: "Comprehension — prose and poem",
  },
  counting: {
    C1:  "Identify numbers 1–50",
    C2:  "Identify numbers 51–100",
    C3:  "Identify numbers 100–999",
    C4:  "Addition without carry",
    C5:  "Addition with carry",
    C6:  "Subtraction without borrow",
    C7:  "Subtraction with borrow",
    C8:  "Tables 1–15",
    C9:  "Multiplication by 1 digit",
    C10: "Multiplication by 2 digits",
    C11: "Division without remainder",
    C12: "Division with remainder",
    C13: "Fractions — basic",
    C14: "Fractions — operations",
    C15: "Decimals — basic",
    C16: "Decimals — operations",
    C17: "Mixed operations",
    C18: "Word problems",
  },
};

const HEAD_CONFIG = {
  reading:  { label: "Reading",  Icon: BookOpen, color: "text-blue-600",   bg: "bg-blue-50",   border: "border-blue-200"   },
  writing:  { label: "Writing",  Icon: PenLine,  color: "text-violet-600", bg: "bg-violet-50", border: "border-violet-200" },
  counting: { label: "Counting", Icon: Hash,     color: "text-teal-600",   bg: "bg-teal-50",   border: "border-teal-200"   },
};

const HEADS = ["reading", "writing", "counting"];

export default function FLNCursorPanel({ learnerId }) {
  const [flnState, setFlnState] = useState(null);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);
  const [saving, setSaving]     = useState(null); // head key currently being saved
  const [assessModal, setAssessModal] = useState(null); // { head } or null

  const load = useCallback(async () => {
    if (!learnerId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await getFLNLearnerState(learnerId);
      setFlnState(data);
    } catch (err) {
      if (err?.response?.status === 404) {
        setError("FLN not yet initialised for this student.");
      } else {
        setError("Could not load FLN state.");
      }
    } finally {
      setLoading(false);
    }
  }, [learnerId]);

  useEffect(() => { load(); }, [load]);

  const handleAction = async (head, newLevel, newStatus) => {
    const prevStatus = flnState?.[head]?.cursor_status;
    setSaving(head);
    try {
      const res = await updateFLNCursor(learnerId, head, newLevel, newStatus);
      setFlnState(res.document);
      if (newStatus === "held") {
        toast.success(`${HEAD_CONFIG[head].label} cursor held`);
      } else if (prevStatus === "held") {
        toast.success(`${HEAD_CONFIG[head].label} cursor released`);
      } else {
        toast.success(`Advanced to ${newLevel}`);
      }
    } catch {
      toast.error("Failed to update FLN cursor");
    } finally {
      setSaving(null);
    }
  };


  return (
    <div className="mt-8 no-print" data-testid="fln-cursor-panel">
      <h2 className="font-teacher text-lg font-semibold text-slate-900 mb-3 flex items-center gap-2">
        <Library className="w-5 h-5" style={{ color: "#E65100" }} />
        FLN Cursors
      </h2>

      {loading ? (
        <div className="flex items-center gap-2 text-slate-400 text-sm py-4">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading FLN state…
        </div>
      ) : error ? (
        <Card className="rounded-xl p-4 border border-amber-200 bg-amber-50">
          <p className="text-sm text-amber-700" data-testid="fln-panel-error">{error}</p>
        </Card>
      ) : flnState ? (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {HEADS.map(head => {
            const conf = HEAD_CONFIG[head];
            const { Icon } = conf;
            const cursor = flnState[head];
            if (!cursor) return null;

            const isHeld    = cursor.cursor_status === "held";
            const levels    = FLN_LEVELS[head];
            const currentIdx = levels.indexOf(cursor.current_level);
            const isTerminal = currentIdx === levels.length - 1;
            const nextLevel  = !isTerminal ? levels[currentIdx + 1] : null;
            const competency = FLN_COMPETENCIES[head]?.[cursor.current_level] ?? "";
            const isSaving   = saving === head;

            return (
              <Card
                key={head}
                className={`rounded-xl p-5 border ${conf.border} bg-white`}
                data-testid={`fln-cursor-card-${head}`}
              >
                {/* Head header */}
                <div className="flex items-center gap-2 mb-3">
                  <div className={`w-8 h-8 rounded-lg ${conf.bg} ${conf.color} flex items-center justify-center`}>
                    <Icon className="w-4 h-4" />
                  </div>
                  <span className="font-semibold text-slate-700">{conf.label}</span>
                </div>

                {/* Level + status + competency */}
                <div className="mb-4">
                  <div className="flex items-center gap-2 mb-1">
                    <span
                      className={`text-xl font-black ${conf.color}`}
                      data-testid={`fln-teacher-level-${head}`}
                    >
                      {cursor.current_level}
                    </span>
                    {isHeld ? (
                      <span
                        className="text-xs font-bold px-2 py-0.5 rounded-full bg-amber-100 text-amber-700"
                        data-testid={`fln-teacher-status-${head}`}
                      >
                        Held
                      </span>
                    ) : (
                      <span
                        className="text-xs font-bold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700"
                        data-testid={`fln-teacher-status-${head}`}
                      >
                        Active
                      </span>
                    )}
                  </div>
                  <p
                    className="text-xs text-slate-500 leading-relaxed"
                    data-testid={`fln-teacher-competency-${head}`}
                  >
                    {competency}
                  </p>
                </div>

                {/* Action buttons */}
                <div className="flex flex-col gap-2">
                  {/* Advance */}
                  <Button
                    size="sm"
                    className="h-8 text-xs bg-slate-800 hover:bg-slate-700 text-white rounded-lg w-full gap-1"
                    disabled={isTerminal || isSaving}
                    onClick={() => handleAction(head, nextLevel, "active")}
                    data-testid={`fln-advance-btn-${head}`}
                  >
                    {isSaving ? (
                      <Loader2 className="w-3 h-3 animate-spin" />
                    ) : isTerminal ? (
                      "Terminal level"
                    ) : (
                      <>Advance <ChevronRight className="w-3 h-3" /> {nextLevel}</>
                    )}
                  </Button>

                  {/* Hold / Release */}
                  {!isHeld ? (
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-8 text-xs border-amber-300 text-amber-700 hover:bg-amber-50 rounded-lg w-full"
                      disabled={isSaving}
                      onClick={() => handleAction(head, cursor.current_level, "held")}
                      data-testid={`fln-hold-btn-${head}`}
                    >
                      Hold
                    </Button>
                  ) : (
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-8 text-xs border-emerald-300 text-emerald-700 hover:bg-emerald-50 rounded-lg w-full"
                      disabled={isSaving}
                      onClick={() => handleAction(head, cursor.current_level, "active")}
                      data-testid={`fln-release-btn-${head}`}
                    >
                      Release
                    </Button>
                  )}

                  {/* Generate Assessment */}
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-8 text-xs border-slate-200 text-slate-500 hover:bg-slate-50 rounded-lg w-full gap-1"
                    onClick={() => setAssessModal({ head })}
                    data-testid={`fln-assess-btn-${head}`}
                  >
                    <ClipboardCheck className="w-3 h-3" /> Assess
                  </Button>
                </div>
              </Card>
            );
          })}
        </div>
      ) : null}

      {/* Assessment modal */}
      {assessModal && flnState?.[assessModal.head] && (
        <FLNAssessmentModal
          head={assessModal.head}
          learnerId={learnerId}
          level={flnState[assessModal.head].current_level}
          onClose={() => setAssessModal(null)}
        />
      )}
    </div>
  );
}
